import { isEqual, orderBy } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { injectIntl, useIntl } from 'react-intl';
import { connect, useDispatch } from 'react-redux';

import {
  builderSetFieldSections,
  builderSetInitialWorkflow,
  builderSetShouldEnableAllComponents,
  builderSetShouldValidate,
  builderSetTab,
} from '~/actions/builder';
import {
  workflowClearFile,
  workflowContractTypeIsRequired,
  workflowLoad,
} from '~/actions/workflow';
import {
  discardDraft,
  getWorkflow,
  patchWorkflow,
  patchWorkflowSettings,
} from '~/api/workflows';
import { trackSegment } from '~/components/SegmentAnalytics';
import { showToast } from '~/components/Shared/EcToast/showToast';
import LockIcon from '~/components/Shared/Icons/LockIcon';
import ErrorIcon from '~/components/Workflow/shared/ErrorIcon';
import { parseDataFieldSections } from '~/components/Workflow/Workflow.utils';
import CreatePartySigner from '~/components/Workflow/WorkflowBuilderPage/CreatePartySigner/CreatePartySigner';
import Edit from '~/components/Workflow/WorkflowBuilderPage/Edit/Edit';
import Finalize from '~/components/Workflow/WorkflowBuilderPage/Finalize/Finalize';
import Form from '~/components/Workflow/WorkflowBuilderPage/Form/Form';
import Review from '~/components/Workflow/WorkflowBuilderPage/Review/Review';
import Settings from '~/components/Workflow/WorkflowBuilderPage/Settings/Settings';
import Setup from '~/components/Workflow/WorkflowBuilderPage/Setup/Setup';
import Sidebar from '~/components/Workflow/WorkflowBuilderPage/Sidebar/Sidebar';
import Sign from '~/components/Workflow/WorkflowBuilderPage/Sign/Sign';
import Source from '~/components/Workflow/WorkflowBuilderPage/Source/Source';
import { Button } from '~/eds/src/components/Button/Button';
import { PageLayout as EdsPageLayout } from '~/eds/src/modules/PageLayout/PageLayout';
import { BuilderTabType } from '~/enums/BuilderTabType';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { withUsers } from '~/hocs/withUsers';
import { useAsync } from '~/hooks/useAsync';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { getErrors } from '~/reducers/workflow';
import { api } from '~/redux/api/createApi';
import { useRouting } from '~/routing/useRouting';
import { ERROR, SUCCESS } from '~/types/toast.types';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import PageLayout from '~/ui/components/PageLayout/PageLayout';
import Text from '~/ui/components/Text/Text';
import { useModal, useModalSimple } from '~/ui/hooks/modal';
import { joinWith } from '~/utils/array';
import { getClientInfo, getUserClientInfo } from '~/utils/user';

function getTabsData({
  shouldEnableAllComponents,
  errors,
  shouldValidate,
  intl,
}) {
  const tooltipMsg = intl.formatMessage({
    id: 'WorkflowBuilder.limitedChangesTooltip',
    defaultMessage:
      'Changes you can make to this workflow are limited since this workflow is no longer active.',
    description: 'Tooltip explaining why workflow changes are limited',
  });
  return {
    source: {
      content: <Source />,
      iconLeft: 'documentOutline',
      iconRight: shouldEnableAllComponents ? (
        <ErrorIcon errors={errors.source} shouldRender={shouldValidate} />
      ) : (
        <LockIcon />
      ),
      id: 'workflow-navs--source',
      label: intl.formatMessage({
        id: 'WorkflowBuilder.sourceTab',
        defaultMessage: 'Source',
        description: 'Source tab label in workflow builder',
      }),
      value: BuilderTabType.Source,
      tooltip: shouldEnableAllComponents ? null : tooltipMsg,
    },
    form: {
      content: <Form />,
      iconLeft: 'field',
      iconRight: shouldEnableAllComponents ? (
        <ErrorIcon errors={errors.form} shouldRender={shouldValidate} />
      ) : (
        <LockIcon />
      ),
      id: 'workflow-navs--form',
      label: intl.formatMessage({
        id: 'WorkflowBuilder.formTab',
        defaultMessage: 'Form',
        description: 'Form tab label in workflow builder',
      }),
      value: BuilderTabType.Form,
      tooltip: shouldEnableAllComponents ? null : tooltipMsg,
    },
    edit: {
      content: <Edit />,
      iconLeft: 'edit',
      iconRight: shouldEnableAllComponents ? (
        <ErrorIcon errors={errors.edit} shouldRender={shouldValidate} />
      ) : (
        <LockIcon />
      ),
      label: intl.formatMessage({
        id: 'WorkflowBuilder.editTab',
        defaultMessage: 'Edit',
        description: 'Edit tab label in workflow builder',
      }),
      value: BuilderTabType.Edit,
      tooltip: shouldEnableAllComponents ? null : tooltipMsg,
    },
    review: {
      content: <Review />,
      iconLeft: 'checklist',
      iconRight: shouldEnableAllComponents ? (
        <ErrorIcon errors={errors.review} shouldRender={shouldValidate} />
      ) : (
        <LockIcon />
      ),
      id: 'workflow-navs--review',
      label: intl.formatMessage({
        id: 'WorkflowBuilder.reviewTab',
        defaultMessage: 'Review',
        description: 'Review tab label in workflow builder',
      }),
      value: BuilderTabType.Review,
      tooltip: shouldEnableAllComponents ? null : tooltipMsg,
    },
    sign: {
      content: <Sign />,
      iconLeft: 'signature',
      iconRight: shouldEnableAllComponents ? (
        <ErrorIcon errors={errors.sign} shouldRender={shouldValidate} />
      ) : (
        <LockIcon />
      ),
      id: 'workflow-navs--sign',
      label: intl.formatMessage({
        id: 'WorkflowBuilder.signTab',
        defaultMessage: 'Sign',
        description: 'Sign tab label in workflow builder',
      }),
      value: BuilderTabType.Sign,
      tooltip: shouldEnableAllComponents ? null : tooltipMsg,
    },
    finalize: {
      content: <Finalize />,
      iconLeft: 'inbox',
      iconRight: shouldEnableAllComponents ? (
        <ErrorIcon errors={errors.finalize} shouldRender={shouldValidate} />
      ) : (
        <LockIcon />
      ),
      id: 'workflow-navs--finalize',
      label: intl.formatMessage({
        id: 'WorkflowBuilder.finalizeTab',
        defaultMessage: 'Finalize',
        description: 'Finalize tab label in workflow builder',
      }),
      value: BuilderTabType.Finalize,
      tooltip: shouldEnableAllComponents ? null : tooltipMsg,
    },
    settings: {
      content: <Settings />,
      iconLeft: 'wrench',
      iconRight: (
        <ErrorIcon errors={errors.settings} shouldRender={shouldValidate} />
      ),
      id: 'workflow-navs--settings',
      label: intl.formatMessage({
        id: 'WorkflowBuilder.settingsTab',
        defaultMessage: 'Settings',
        description: 'Settings tab label in workflow builder',
      }),
      value: BuilderTabType.Settings,
    },
  };
}

function Page({
  // connected,
  errors,
  folderTree,
  selectedTab,
  shouldEditParties,
  shouldEditSetup,
  shouldEnableAllComponents,
  shouldValidate,
  workflow,
  workflowClearFile,
  builderSetShouldValidate,
  workflowLoad,
  builderSetShouldEnableAllComponents,
  builderSetTab,
  builderSetFieldSections,
  builderSetInitialWorkflow,
  hasUnsavedChanges: initalHasUnsavedChanges,
}) {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { navigate, params } = useRouting();
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(
    initalHasUnsavedChanges,
  );
  const currentUser = useCurrentUser();
  const clientId = currentUser.client;
  const { data: dataFieldSections } =
    api.endpoints.getDataFieldSections.useQuery(clientId);
  const isContractTypeEnabled = useFlag(FlagType.WorkflowContractType);

  useEffect(
    () => setHasUnsavedChanges(initalHasUnsavedChanges),
    [initalHasUnsavedChanges],
  );

  useEffect(() => {
    if (
      isContractTypeEnabled &&
      workflow?.wssDesignated &&
      !workflow?.published
    ) {
      dispatch(workflowContractTypeIsRequired());
    }
  }, [isContractTypeEnabled, workflow?.wssDesignated]);

  useEffect(() => {
    if (dataFieldSections) {
      const parsedDataFieldSections = parseDataFieldSections(dataFieldSections);
      builderSetFieldSections(parsedDataFieldSections);
    }
  }, [dataFieldSections]);

  const workflowHasErrors = Object.values(errors).some(
    (error) => error.count > 0,
  );
  const { workflowId } = params;
  const workflowWithPublishFlag = {
    ...workflow,
    publishable: !workflowHasErrors,
  };
  const hasPrefilledFormData = !!workflow.prefilledFormData;

  function goBack() {
    navigate(-1);
  }

  function goBackConfirmChanges() {
    if (hasUnsavedChanges) {
      const confirm = window.confirm(
        intl.formatMessage({
          id: 'WorkflowBuilder.unsavedChangesConfirm',
          defaultMessage:
            'You have unsaved changes that will be lost if you decide to continue. Are you sure you want to leave this page?',
          description:
            'Confirmation dialog when leaving page with unsaved changes',
        }),
      );
      if (confirm) {
        goBack();
      }
    } else {
      goBack();
    }
  }

  const trackSegmentWorkflow = (name, newWorkflowId) => {
    trackSegment(name, {
      groupId: getClientInfo(currentUser),
      userId: getUserClientInfo(currentUser),
      workflowId: newWorkflowId ?? workflow.id,
      workflowName: workflow.name,
    });
  };

  const { executor: fetchWorkflow, isLoading: isLoadingWorkflow } = useAsync(
    getWorkflow,
    { id: workflowId, folderTreeId: folderTree.id },
    {
      condition: workflowId,
      deps: [folderTree, workflowId],
      errorHandler: goBack,
      successHandler: (response) => {
        builderSetShouldValidate(true);
        workflowLoad(response);
        builderSetShouldEnableAllComponents(response);
        builderSetInitialWorkflow(response);
      },
      errorToastMessage: intl.formatMessage({
        id: 'WorkflowBuilder.loadWorkflowError',
        defaultMessage: 'Failed to load workflow, please try again.',
        description: 'Error message when workflow fails to load',
      }),
    },
  );

  const { executor: executeDiscardDraft } = useAsync(discardDraft, workflow, {
    errorToastMessage: intl.formatMessage(
      {
        id: 'WorkflowBuilder.discardDraftError',
        defaultMessage:
          'Unable to discard draft "{workflowName}", please try again.',
        description:
          'Error message when unable to discard workflow draft, Example: workflowName = "My Workflow Draft"',
      },
      { workflowName: workflow.name },
    ),
    successHandler: () => {
      const segmentName = isNew
        ? 'User Deletes Workflow Draft'
        : 'User Reverts Workflow Draft';
      trackSegmentWorkflow(segmentName);
      goBack();
    },
    successToastMessage: intl.formatMessage(
      {
        id: 'WorkflowBuilder.discardDraftSuccess',
        defaultMessage:
          'Workflow "{workflowName}" draft has been successfully discarded.',
        description:
          'Success message when workflow draft is discarded. Example: workflowName = "My Workflow Draft"',
      },
      { workflowName: workflow.name },
    ),
  });

  const { executor: executePatchWorkflow, isLoading: isSaving } = useAsync(
    patchWorkflow,
    workflowWithPublishFlag,
    {
      errorToastMessage: intl.formatMessage(
        {
          id: 'WorkflowBuilder.saveWorkflowError',
          defaultMessage:
            'Unable to save workflow "{workflowName}", please try again.',
          description:
            'Error message when unable to save workflow. Example: workflowName = "My Workflow Draft"',
        },
        { workflowName: workflow.name },
      ),
      successHandler: (response) => {
        workflowClearFile();
        setHasUnsavedChanges(false);
        trackSegmentWorkflow('User Saves Workflow Draft', response.id);
        navigate(`/workflow/builder/${response.id}`, { replace: true });
      },
      successToastMessage: intl.formatMessage(
        {
          id: 'WorkflowBuilder.saveWorkflowSuccess',
          defaultMessage:
            'Workflow "{workflowName}" has been successfully saved.',
          description:
            'Success message when workflow is saved. Example: workflowName = "My Workflow Draft"',
        },
        { workflowName: workflow.name },
      ),
    },
  );

  const {
    executor: executePatchWorkflowSettings,
    isLoading: isSavingSettings,
  } = useAsync(patchWorkflowSettings, workflow, {
    errorToastMessage: intl.formatMessage(
      {
        id: 'WorkflowBuilder.saveWorkflowSettingsError',
        defaultMessage:
          'Unable to save workflow "{workflowName}", please try again.',
        description:
          'Error message when unable to save workflow settings. Example: workflowName = "My Workflow Draft"',
      },
      { workflowName: workflow.name },
    ),
    successHandler: () => {
      setHasUnsavedChanges(false);
      fetchWorkflow();
    },
    successToastMessage: intl.formatMessage(
      {
        id: 'WorkflowBuilder.saveWorkflowSettingsSuccess',
        defaultMessage:
          'Workflow "{workflowName}" has been successfully saved.',
        description:
          'Success message when workflow settings are saved. Example: workflowName = "My Workflow Draft"',
      },
      { workflowName: workflow.name },
    ),
  });

  const [saveAndPublishWorkflowTrigger, { isLoading: isPublishing }] =
    api.endpoints.saveAndPublishWorkflow.useMutation();

  const executeSaveAndPublishWorkflow = () => {
    saveAndPublishWorkflowTrigger(workflow)
      .unwrap()
      .then((response) => {
        workflowClearFile();
        setHasUnsavedChanges(false);
        trackSegmentWorkflow('User Publishes Workflow Draft', response.id);
        navigate(`/workflow/builder/${response.id}`, { replace: true });
        showToast(
          SUCCESS,
          intl.formatMessage(
            {
              id: 'WorkflowBuilder.publishWorkflowSuccess',
              defaultMessage:
                'Workflow "{workflowName}" has been successfully published.',
              description:
                'Success message when workflow is published. Example: workflowName = "My Workflow Draft"',
            },
            { workflowName: workflow.name },
          ),
        );
      })
      .catch(() => {
        showToast(
          ERROR,
          intl.formatMessage(
            {
              id: 'WorkflowBuilder.publishWorkflowDirectError',
              defaultMessage:
                'Unable to publish workflow "{workflowName}", please try again.',
              description:
                'Error message when unable to publish workflow directly',
            },
            { workflowName: workflow.name },
          ),
        );
      });
  };

  const [publishModal, showPublishModal] = useModal({
    actionButton: {
      errorHandler: () => {
        showToast(
          ERROR,
          intl.formatMessage(
            {
              id: 'WorkflowBuilder.publishWorkflowError',
              defaultMessage:
                'Unable to publish workflow "{workflowName}", please try again.',
              description:
                'Error message when unable to publish workflow. Example: workflowName = "My Workflow Draft"',
            },
            { workflowName: workflow.name },
          ),
        );
      },
      promise: async () => {
        const response = await saveAndPublishWorkflowTrigger(workflow).unwrap();
        trackSegmentWorkflow('User Publishes Workflow Draft', response.id);
        navigate(`/workflow/builder/${response.id}`, { replace: true });
      },
      text: intl.formatMessage({
        id: 'WorkflowBuilder.resetMappingAndPublish',
        defaultMessage: 'Reset Mapping & Publish',
        description:
          'Button text for resetting mapping and publishing workflow',
      }),
    },
    title: intl.formatMessage({
      id: 'WorkflowBuilder.publishWorkflowTitle',
      defaultMessage: 'Publish Workflow',
      description: 'Title for publish workflow modal',
    }),
    content: (
      <Text color="gray-900" variant="s-spaced">
        {intl.formatMessage({
          id: 'WorkflowBuilder.publishWorkflowWarning',
          defaultMessage:
            'Republishing the workflow will necessitate re-mapping and re-generating your Prefillable Form Link in Settings.',
          description: 'Warning message about republishing workflow',
        })}
      </Text>
    ),
  });

  const isNew = workflow.versions.length === 1;
  const discardDraftTitle = isNew
    ? intl.formatMessage({
        id: 'WorkflowBuilder.deleteDraftTitle',
        defaultMessage: 'Delete Draft',
        description: 'Title for delete draft modal',
      })
    : intl.formatMessage({
        id: 'WorkflowBuilder.revertDraftTitle',
        defaultMessage: 'Revert Draft',
        description: 'Title for revert draft modal',
      });
  const discardDraftDescription = isNew
    ? intl.formatMessage({
        id: 'WorkflowBuilder.deleteDraftDescription',
        defaultMessage: 'Do you want to delete this draft?',
        description: 'Description for delete draft confirmation',
      })
    : intl.formatMessage({
        id: 'WorkflowBuilder.revertDraftDescription',
        defaultMessage:
          'Do you want to discard all saved changes that have been made since the last time the form was published?',
        description: 'Description for revert draft confirmation',
      });
  const [discardDraftModal, showDiscardDraftModal] = useModalSimple({
    confirm: executeDiscardDraft,
    text: isNew
      ? intl.formatMessage({
          id: 'WorkflowBuilder.deleteButton',
          defaultMessage: 'Delete',
          description: 'Delete button text',
        })
      : intl.formatMessage({
          id: 'WorkflowBuilder.revertButton',
          defaultMessage: 'Revert',
          description: 'Revert button text',
        }),
    title: discardDraftTitle,
    content: (
      <Text color="gray-700" variant="s-spaced">
        {intl.formatMessage(
          {
            id: 'WorkflowBuilder.discardDraftWarning',
            defaultMessage: '{description} This action cannot be undone.',
            description:
              'Warning message for discarding draft action. Example: description = "any text"',
          },
          { description: discardDraftDescription },
        )}
      </Text>
    ),
    width: 'm',
  });

  const isLoading =
    isLoadingWorkflow || isSaving || isSavingSettings || isPublishing;

  const tooltipErrorMessage = useMemo(() => {
    const errorTypes = orderBy(
      Object.entries(errors)
        .filter(([_type, { count }]) => count)
        .map(([type]) => type),
    );
    const errorTypesString = joinWith(errorTypes, {
      delimiter: ', ',
      lastDelimiter: ' and ',
    });
    return intl.formatMessage(
      {
        id: 'WorkflowBuilder.errorTooltip',
        defaultMessage:
          'There is an error in your {errorTypes} {stepCount, plural, one {step} other {steps}} please edit and try again',
        description:
          'Tooltip message showing workflow errors. Example: errorTypes = "source, form and review", stepCount = number of error types',
      },
      {
        errorTypes: errorTypesString,
        stepCount: errorTypes.length,
      },
    );
  }, [errors, intl]);

  const header = (
    <PageLayout.Header
      leftIcon={{
        icon: 'close',
        onClick: goBackConfirmChanges,
      }}
      rightContent={
        <FlexLayout space={4}>
          {shouldEnableAllComponents ? (
            <React.Fragment>
              <Button
                disabled={!workflow.isDraft}
                id="workflow--discard-draft"
                text={discardDraftTitle}
                onClick={showDiscardDraftModal}
              />
              <Button
                disabled={!hasUnsavedChanges}
                id="workflow--save-draft"
                text={intl.formatMessage({
                  id: 'WorkflowBuilder.saveDraftButton',
                  defaultMessage: 'Save Draft',
                  description: 'Save draft button text',
                })}
                onClick={executePatchWorkflow}
              />
              <Button
                disabled={workflowHasErrors}
                tooltip={workflowHasErrors && tooltipErrorMessage}
                iconLeft="checkmark"
                id="workflow--publish"
                text={intl.formatMessage({
                  id: 'WorkflowBuilder.publishButton',
                  defaultMessage: 'Publish',
                  description: 'Publish button text',
                })}
                variant="primary"
                onClick={() =>
                  hasPrefilledFormData
                    ? showPublishModal()
                    : executeSaveAndPublishWorkflow()
                }
              />
            </React.Fragment>
          ) : (
            <Button
              disabled={!hasUnsavedChanges}
              text={intl.formatMessage({
                id: 'WorkflowBuilder.saveChangesButton',
                defaultMessage: 'Save Changes',
                description: 'Save changes button text',
              })}
              variant="primary"
              onClick={executePatchWorkflowSettings}
            />
          )}
        </FlexLayout>
      }
      title={{
        text: workflow.name || '',
      }}
    />
  );

  const tabsData = getTabsData({
    shouldEnableAllComponents,
    errors,
    shouldValidate,
    intl,
  });
  const tabs = Object.values(tabsData);

  const sidebar = (
    <PageLayout.Sidebar>
      <Sidebar />
    </PageLayout.Sidebar>
  );

  if (shouldEditSetup) {
    return <Setup />;
  }

  return (
    <>
      {isLoading ? (
        <EdsPageLayout
          loadingContent={{
            isLoading,
          }}
        />
      ) : shouldEditParties ? (
        <PageLayout>
          <CreatePartySigner />
        </PageLayout>
      ) : (
        <PageLayout
          bg="background.quiet"
          header={header}
          sidebar={sidebar}
          selectedTab={selectedTab}
          tabs={tabs}
          onSelectTab={builderSetTab}
        >
          {tabsData[selectedTab].content}
        </PageLayout>
      )}
      {discardDraftModal}
      {publishModal}
    </>
  );
}

function mapStateToProps({ workflow, builder, client }, { users, intl }) {
  const {
    selectedTab,
    shouldEditParties,
    shouldEditSetup,
    shouldEnableAllComponents,
    shouldValidate,
  } = builder;
  const {
    prefilledFormData: _omittedPrefilled,
    contractTypeIsRequired: _omittedContractTypeIsRequired,
    ...restWorkflow
  } = workflow;

  const {
    prefilledFormData: _omittedOriginalPrefilled,
    contractTypeIsRequired: _omittedOriginalContractTypeIsRequired,
    ...restOriginalWorkflow
  } = builder.initialWorkflow;

  return {
    errors: getErrors(workflow, builder, client, users, intl),
    folderTree: client.folderTree,
    workflow,
    selectedTab,
    shouldEditParties,
    shouldEditSetup,
    shouldEnableAllComponents,
    shouldValidate,
    hasUnsavedChanges: !isEqual(restWorkflow, restOriginalWorkflow),
  };
}

export default injectIntl(
  withUsers(
    connect(mapStateToProps, {
      workflowClearFile,
      builderSetFieldSections,
      builderSetShouldEnableAllComponents,
      builderSetShouldValidate,
      builderSetTab,
      builderSetInitialWorkflow,
      workflowLoad,
    })(Page),
  ),
);
